import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography
} from "@mui/material"

import { findSharedPrompt, LivePrompt } from "../livePrompts"
import {
  GUIDE_PROMPTS,
  GuidePrompt,
  PROMPT_LAYER_LADDER,
  PROMPT_STRUCTURES
} from "./guideContent"
import { Card, GuideSection } from "./guideParts"

const LAYER_COLOR: Record<GuidePrompt["layer"], string> = {
  v2: "#7B4DFF",
  BASE: "#5F6368"
}

function PromptCard({
  prompt,
  live,
  loading
}: {
  prompt: GuidePrompt
  live?: LivePrompt
  loading: boolean
}) {
  const color = LAYER_COLOR[prompt.layer]
  return (
    <Card accent={color}>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={prompt.layer === "v2" ? "v2'ye özel" : "v1 ile ortak (BASE)"}
          sx={{ bgcolor: `${color}1A`, color, fontWeight: 800 }}
        />
        <Chip
          size="small"
          variant="outlined"
          label={prompt.required ? "Zorunlu" : "Opsiyonel"}
        />
        {live?.aiModel && (
          <Chip size="small" variant="outlined" label={live.aiModel} />
        )}
      </Stack>
      <Typography
        variant="subtitle2"
        component="code"
        sx={{ fontWeight: 900, display: "block", mt: 1.5 }}
      >
        {prompt.name}
      </Typography>
      <Typography variant="body2" sx={{ lineHeight: 1.55, mt: 1 }}>
        {prompt.purpose}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block", mt: 1 }}
      >
        <strong>Ne zaman çalışır: </strong>
        {prompt.when}
      </Typography>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Canlı satır yükleniyor
          </Typography>
        </Stack>
      )}
      {!loading && !live && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Bu satır bu ortamda bulunamadı.
          {prompt.required ? " v2 turları bu satır olmadan çalışmaz." : ""}
        </Alert>
      )}
      {live?.description && (
        <Accordion
          variant="outlined"
          disableGutters
          sx={{ mt: 1.5, "&:before": { display: "none" } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="caption" sx={{ fontWeight: 800 }}>
              Canlı metni göster ({live.description.length.toLocaleString("tr")}{" "}
              karakter)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.25,
                bgcolor: "#111827",
                color: "#F9FAFB",
                borderRadius: 1,
                whiteSpace: "pre-wrap",
                overflow: "auto",
                fontSize: 12,
                lineHeight: 1.45,
                maxHeight: 360
              }}
            >
              {live.description}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Card>
  )
}

export default function PromptsSection({
  prompts,
  loading,
  failed
}: {
  prompts: LivePrompt[]
  loading: boolean
  failed: boolean
}) {
  return (
    <GuideSection
      id="prompts"
      eyebrow="Veritabanındaki talimatlar"
      title="Promptlar: neden v2'nin sadece iki satırı var?"
      lead="Ortada tek bir ajan olduğu için. v1'de her aşamanın kendi prompt satırı vardı; v2'de hepsi CONVERSATION_AGENT satırında birleşti. Bir tur içinde v2 ayrıca v1 ile ortak iki satırı daha okur. Diğer promptlar silinmedi: v1 ve konuşma dışındaki özellikler (ürün SSS, çeviri, analiz…) onları kullanmaya devam ediyor."
    >
      {failed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Canlı promptlar alınamadı; açıklamalar gösteriliyor.
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {GUIDE_PROMPTS.map((prompt) => (
          <Grid item xs={12} md={6} key={prompt.name}>
            <PromptCard
              prompt={prompt}
              live={findSharedPrompt(
                prompts,
                prompt.name,
                prompt.layer === "v2" ? "v2" : null
              )}
              loading={loading}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.5 }}>
              Tek satırda her şey
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1.5 }}
            >
              Koşula göre değişen metin kodda değil, satırın içinde yazılır.
              Böylece admin satırı baştan sona tek parça okuyabilir.
            </Typography>
            <Stack spacing={1.5}>
              {PROMPT_STRUCTURES.map((item) => (
                <Box key={item.name}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {item.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="code"
                    sx={{ display: "block", color: "#7B4DFF" }}
                  >
                    {item.syntax}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.meaning}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.5 }}>
              v2 hangi satırı okur?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1.5 }}
            >
              Yukarıdan aşağı bakar, ilk bulduğunu kullanır. v1 sadece BASE
              okur; bu yüzden v1’e geri dönmek prompta dokunmayı gerektirmez.
            </Typography>
            <Stack component="ol" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
              {PROMPT_LAYER_LADDER.map((rung) => (
                <Typography component="li" key={rung} variant="body2">
                  {rung}
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </GuideSection>
  )
}
